import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { SurveyAnswerJson, SurveyModelJson, SurveyQuestionJson } from "@performyard/service-contracts";

import { PYSurveyContainer } from "../components/PYSurveyContainer";
import AuthorSurveyModals from "./components/AuthorSurveyModals";
import { Survey } from "./components/Survey";

import { AUTHOR_SURVEY_MODALS } from "./store/model";
import { useActions, useAnswers, useSetters } from "./store/store";

import { useCreateSurveyResponse } from "../../../../../../api/hooks";
import useUnsavedChanges from "../../../../../../hooks/useUnsavedChanges";

export const AuthorSurvey = ({ survey }: { survey: SurveyModelJson }) => {
	const { surveyLinkId = "" } = useParams<{ surveyLinkId: string }>();
	const { mutate: submitResponse, isPending } = useCreateSurveyResponse(survey._id, surveyLinkId, {
		onSuccess: () => {
			actions.clearAnswers();
		},
	});
	const [submitting, setSubmitting] = useState(false);
	const sortedQuestions = (survey?.questions || [])
		.filter((question) => !question.deleted)
		.sort((a, b) => a.order - b.order) as SurveyQuestionJson[];
	const setters = useSetters();
	const actions = useActions();
	const answers = useAnswers();

	const { setIsUnsafe, setIsSafe } = useUnsavedChanges();

	useEffect(() => {
		setters.setQuestions(sortedQuestions);
		return () => {
			setIsSafe();
		};
	}, []);

	useEffect(() => {
		if (isPending) {
			setSubmitting(true);
		} else {
			setSubmitting(false);
		}
	}, [isPending]);

	useEffect(() => {
		if (Object.keys(answers).length > 0) {
			setIsUnsafe();
		} else {
			setIsSafe();
		}
	}, [answers]);

	const getUnansweredQuestions = useCallback(
		(answersParam: Record<string, SurveyAnswerJson["value"]>) => {
			const answerableQuestions = sortedQuestions.filter((question) => question.type !== "instructions");
			const answerableQuestionsIds = answerableQuestions.map((question) => question._id);
			return answerableQuestionsIds.filter((id) => !answersParam[id]);
		},
		[sortedQuestions]
	);

	const handleSubmit = (answersParam: Record<string, SurveyAnswerJson["value"]>) => {
		if (!submitting) {
			const unansweredQuestions = getUnansweredQuestions(answersParam);
			if (unansweredQuestions.length === 0) {
				const answersJson = Object.fromEntries(
					Object.entries(answersParam).map(([key, value]) => [
						key,
						Array.isArray(value) ? value.map(String) : String(value),
					])
				);
				submitResponse({ answers: answersJson });
			} else {
				setters.setModal(AUTHOR_SURVEY_MODALS.SUBMIT_INCOMPLETE_SURVEY);
			}
		}
	};

	const handleSubmitFromModal = ({
		answers: answersParam,
	}: {
		answers: Record<string, SurveyAnswerJson["value"]>;
	}) => {
		const filteredAnswers = Object.fromEntries(
			Object.entries(answersParam).map(([key, value]) => [
				key,
				Array.isArray(value) ? value.map(String) : String(value),
			])
		);
		submitResponse({ answers: filteredAnswers });
	};

	return (
		<PYSurveyContainer testId="author-survey">
			<Survey surveyName={survey.name} questions={sortedQuestions} onSubmit={handleSubmit} />
			<AuthorSurveyModals
				submitting={submitting}
				submitResponse={handleSubmitFromModal}
				getUnansweredQuestions={getUnansweredQuestions}
			/>
		</PYSurveyContainer>
	);
};
